// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { awOrange, awPanelGrey, awBorderGrey, awMutedGrey } from 'Utils/themes';
import { getStatusDotColor, PinStatusKind } from 'Features/map/EsriDashboardMap/ProjectOverviewCard/statusChip';

// Figma spec: a 520px panel on a 1963px frame - 26.49% of the viewport - with 460px of
// content inside ~30px gutters. The rail takes that same viewport fraction here, clamped so
// tiny windows keep a usable rail and huge ones don't overgrow the frame's design size.
export const RAIL_WIDTH_CSS = 'clamp(360px, 26.49vw, 520px)';

// A design-frame pixel value, applied at the same proportion of the rail's actual width
// (ratio = px / the frame's 520px rail). At full width these resolve to the Figma's exact px.
const railRel = (designPx: number): string => `calc(${(designPx / 520).toFixed(4)} * ${RAIL_WIDTH_CSS})`;

const styles = (theme: any) => ({
  // The Figma's left rail: search on top, the sort/filter row, then the scrolling card list.
  // Explicit height for Safari, whose flex-stretch sizing is unreliable down nested columns.
  root: {
    width: RAIL_WIDTH_CSS,
    minWidth: RAIL_WIDTH_CSS,
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: awPanelGrey,
    boxShadow: '4px 8px 8px rgba(0, 0, 0, 0.06)',
    // The frame's 30px gutters and 20px section gap, as proportions of the rail.
    padding: `${railRel(30)} ${railRel(30)} 0`,
    gap: railRel(20),
    // The map sits beside the rail; the spec's edge shadow must paint over it.
    zIndex: 1,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  // Type scales with the rail like everything else, with px floors so text on a narrow rail
  // never drops below legibility.
  headerTitle: {
    fontSize: `max(${railRel(18)}, 13px)`,
    lineHeight: railRel(25),
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: theme.palette.text.primary,
    flex: 1,
  },
  headerSelect: {
    fontSize: `max(${railRel(12)}, 10px)`,
    fontWeight: 500,
    letterSpacing: '0.04em',
    color: awMutedGrey,
  },
  cardList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    // The Figma stacks cards on a ~159px pitch for a ~149px card: a 10px gap.
    gap: railRel(10),
    paddingBottom: railRel(16),
  },
  // Figma card: white, 12px radius, hairline border, 20px padding, soft drop shadow.
  // Explicit width/floor because Safari lays out styled interactive elements unreliably;
  // the floor guarantees a visible card even if content sizing misbehaves.
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: railRel(8),
    width: '100%',
    // Never let the scroll column compress cards to fit its height - a shrunk card clips its
    // date lines and footer behind overflow:hidden.
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: railRel(12),
    border: `1px solid ${awBorderGrey}`,
    boxShadow: '2px 6px 4px rgba(0, 0, 0, 0.1)',
    textAlign: 'left',
    padding: railRel(20),
    cursor: 'pointer',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: theme.shadows[3],
    },
    // The Figma keeps each card's "..." menu in the markup but invisible until hover.
    '&:hover .railCardMenu': {
      opacity: 1,
    },
  },
  // Figma selected state: the chosen card swaps its hairline border for the accent blue.
  cardSelected: {
    borderColor: theme.palette.primary.main,
  },
  cardBody: {
    // Figma: the 83px thumbnail's text column starts at 95px - a 12px gap.
    display: 'flex',
    gap: railRel(12),
  },
  textCol: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  // Hidden on idle cards per the Figma; hover (rule on the card) or selection reveals it.
  cardMenuButton: {
    width: railRel(30),
    height: railRel(30),
    padding: 0,
    // The Figma overlaps the 30px ellipse with the title line; pulling the button out of the
    // row's flow keeps the name row at text height.
    margin: `${railRel(-4)} 0 ${railRel(-5)}`,
    '& svg': {
      fontSize: railRel(20),
    },
    color: theme.palette.text.primary,
    opacity: 0,
    transition: 'opacity 120ms',
    '&:focus-visible': {
      opacity: 1,
    },
  },
  cardMenuButtonVisible: {
    opacity: 1,
  },
  // The minimap: both axes carry the frame's own measurements (83x71 inside the 520 rail),
  // scaled by the same rail factor - so every edge sits at its Figma-relative position and
  // the 83:71 aspect holds exactly at any rail size.
  cardThumb: {
    width: railRel(83),
    height: railRel(71),
    flexShrink: 0,
    alignSelf: 'flex-start',
    objectFit: 'cover',
    borderRadius: railRel(8),
    border: `1px solid ${awBorderGrey}`,
  },
  // Figma type scale: title 18 Medium with tracking, metadata 12 with 0.5px tracking.
  // The design's titles never wrap (whitespace-nowrap in the source frame) - long names
  // truncate so every card keeps the frame's ~460x149 proportions.
  // Every text row pins its line box to the frame's geometry (railRel line-heights), so the
  // column - and the card's height ratio - never move; the fonts float inside those boxes
  // with px floors for legibility on scaled-down rails. Tracking is em-based to follow.
  cardName: {
    fontSize: `max(${railRel(18)}, 13px)`,
    fontWeight: 500,
    lineHeight: railRel(25),
    letterSpacing: '0.04em',
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Figma rhythm: the status row hugs the title (~2px) with ~6px down to the dates.
  // The frame's exact column offsets: the status row starts 22px from the title's top -
  // slightly inside its descender space - and the dates start at 42px.
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: railRel(8),
    margin: `${railRel(-3)} 0 ${railRel(4)}`,
  },
  statusDot: (kind: PinStatusKind) => ({
    width: railRel(10),
    height: railRel(10),
    minWidth: railRel(10),
    borderRadius: '50%',
    backgroundColor: getStatusDotColor(theme, kind),
  }),
  statusLabel: {
    fontSize: `max(${railRel(12)}, 10px)`,
    lineHeight: railRel(16),
    letterSpacing: '0.04em',
    color: theme.palette.text.primary,
  },
  // The unlocated-project marker: a slashed map pin whose tooltip carries the explanation.
  noLocationIcon: {
    fontSize: `max(${railRel(18)}, 14px)`,
    color: awOrange,
  },
  cardDates: {
    fontSize: `max(${railRel(12)}, 10px)`,
    // Half the frame's 35px two-line dates block per line.
    lineHeight: railRel(17.5),
    letterSpacing: '0.04em',
    color: theme.palette.text.primary,
    '& b': {
      fontWeight: 500,
    },
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${awBorderGrey}`,
    paddingTop: railRel(8),
  },
  cardRef: {
    flex: 1,
    minWidth: 0,
    fontSize: `max(${railRel(12)}, 10px)`,
    lineHeight: railRel(16),
    letterSpacing: '0.02em',
    color: theme.palette.text.primary,
    opacity: 0.7,
  },
  cardPrice: {
    flexShrink: 0,
    marginLeft: railRel(8),
    fontSize: `max(${railRel(14)}, 11px)`,
    lineHeight: railRel(16),
    letterSpacing: '0.02em',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  emptyState: {
    color: awMutedGrey,
    textAlign: 'center',
    marginTop: theme.spacing(6),
  },
});

export default styles;
